import { formatDate, isBefore } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";

import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Box, LinearProgress, Typography } from "@mui/material";

import { CampaignAnalyticsService } from "@analytics/index";

import Overlay from "@components/Overlay/Overlay";
import PageContainer from "@components/PageContainer";
import { IBreadcrumb } from "@components/Slides/BreadcrumbsStepper";
import { SlideStepper, SlideStepperBreadcrumbs, SlideStepperCancelButton } from "@components/Slides/SlideStepper";
import TabGroup from "@components/Tabs/TabGroup";

import { ICancelScheduledCampaignProperties } from "@pages/Campaigns/components/CampaignsTable";

import { useCampaignQuery, useCancelScheduledEmailQuery } from "@queries/campaign.query";

import useCampaignEmailStore from "@store/campaignStore";
import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

import CampaignContactsEmails from "./CampaignContactsEmails";
import CampaignPerformance from "./CampaignPerformance";

const handleCancelScheduledCampaign = ({
  campaignId,
  cancelScheduledCampaign,
  dateTime,
  technologyId,
}: ICancelScheduledCampaignProperties) => {
  PopupActions.openConfirmation({
    content: (
      <Typography sx={{ color: theme.palette.text.primary, mt: 1 }}>
        Are you sure you would like to cancel sending this campaign on <strong>{dateTime}</strong>? You{" "}
        <strong>
          <u>cannot</u>
        </strong>{" "}
        undo this action.
      </Typography>
    ),
    customOnConfirmText: "Cancel Send",
    customOnRejectText: "Dismiss",
    isDanger: true,
    onConfirm: () => {
      if (!campaignId) return;
      void cancelScheduledCampaign({ campaignId, technologyId });
    },
    title: "Cancel Scheduled Send",
  });
};

export default function ViewCampaign() {
  const navigate = useNavigate();
  const parameters = useParams();

  const { setSelectedEmailList } = useCampaignEmailStore();

  useEffect(() => {
    return () => {
      setSelectedEmailList([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: cancelScheduledCampaign, status: cancelCampaignStatus } = useCancelScheduledEmailQuery();

  const { data: campaign, status } = useCampaignQuery(parameters.campaignId);

  const isOldCampaign = campaign?.created ? isBefore(new Date(campaign?.sentAt ?? ""), new Date(2024, 6, 15)) : false;
  const isScheduledCampaign = campaign && !!campaign?.scheduledAt;
  const isDraftCampaign = campaign && !campaign?.sentAt && !campaign?.scheduledAt;

  useEffect(() => {
    if (!campaign) return;
    if (cancelCampaignStatus === "success") {
      navigate(`/portfolio/technology/${campaign.technologyId}/campaigns`);
    }
  }, [cancelCampaignStatus]);

  const tabs = useMemo(
    () => [
      { label: "Performance", tooltip: "View email performance metrics." },
      { disabled: isOldCampaign || isScheduledCampaign, label: "Contacts", tooltip: "View contacts you've reached." },
    ],
    [],
  );

  const [tabIndex, setTabIndex] = useState(() => {
    const foundIndex = tabs.findIndex((tab) => tab.label.toLowerCase() === parameters.tabIdx);
    return Math.max(foundIndex, 0);
  });

  const handleCancel = () => {
    if (!campaign || !campaign.scheduledAt) return;
    const date = formatDate(new Date(campaign.scheduledAt), "MM/dd/yyyy, 'at' hh:mm a");
    handleCancelScheduledCampaign({
      campaignId: campaign.id,
      cancelScheduledCampaign,
      dateTime: date,
      technologyId: campaign.technologyId,
    });
  };

  const handleTabSwitch = (index: number) => {
    setTabIndex(index);
    const tab = tabs[index]?.label.toLowerCase() ?? tabs[0]!.label.toLowerCase();
    navigate(`/campaigns/${parameters.campaignId}/${tab}`);
  };

  if (isDraftCampaign) {
    return <Navigate to={`/portfolio`} />;
  }

  if (!parameters.campaignId) {
    return <Navigate to="/portfolio" />;
  }

  if (status === "pending") {
    return <LinearProgress />;
  }

  if (!campaign) {
    return <Navigate to="/portfolio" />;
  }

  const breadcrumbs: IBreadcrumb[] = [
    {
      clickable: true,
      icon: LightbulbRoundedIcon,
      label: "Technology",
      onClick: () => navigate(`/portfolio/technology/${campaign.technologyId}/companies`),
      tooltipText: "View your technology portfolio.",
    },
    {
      clickable: true,
      icon: SendRoundedIcon,
      label: "Campaigns",
      onClick: () => navigate(`/portfolio/technology/${campaign.technologyId}/campaigns`),
      tooltipText: "Create a campaign for this technology.",
    },
    {
      clickable: false,
      icon: InsertChartRoundedIcon,
      label: campaign.name,
      onClick: () => {
        /* Do Nothing */
      }, // assign an empty function to onClick
      tooltipText: "View the campaign dashboard.",
    },
  ];

  return (
    <PageContainer>
      <Overlay
        caption="Please do not reload page."
        isVisible={cancelCampaignStatus === "pending"}
        title="Canceling Scheduled Campaign"
      />
      <SlideStepper>
        {/* index is hardcoded to 2 because the first two steps are not part of this page */}
        <SlideStepperBreadcrumbs
          breadcrumbs={breadcrumbs}
          handleClickIndex={(index) => breadcrumbs[index]?.onClick()}
          index={2}
          workflow="CampaignDashboard"
        />
        {isScheduledCampaign && <SlideStepperCancelButton cancelButtonText="Cancel Send" handleCancel={handleCancel} />}
      </SlideStepper>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: "100%",
        }}
      >
        <TabGroup index={parameters["*"] === "performance" ? 0 : 1} setIndex={handleTabSwitch} tabs={tabs} />

        <Routes>
          <Route
            element={<CampaignPerformance campaign={campaign} isOldCampaign={isOldCampaign} />}
            path="/performance"
          />
          <Route
            element={
              <CampaignContactsEmails
                campaign={campaign}
                emails={campaign.emails}
                technologyId={campaign.technologyId}
              />
            }
            path="/contacts"
          />
        </Routes>
      </Box>
    </PageContainer>
  );
}
